import React from 'react';

import { arrayValidation } from 'utils/array';

import type { Column } from 'api/shared/models';

const paddingDefault = 8;

export const calculateColumn = (columns: Column[], apiField: string) => {
  if (!arrayValidation(columns)) return 0;
  const tempHeaderRows = columns.filter(
    (item) => item.fixedColumn && item.status,
  );

  if (!arrayValidation(tempHeaderRows)) return 0;

  let width = 0;
  for (let index = 0; index < tempHeaderRows.length; index++) {
    const element = tempHeaderRows[index];

    if (element.apiField === apiField) {
      break;
    }
    width += Number(element.width) + paddingDefault;
  }
  return width;
};

type StyleProps = {
  index?: number;
  column: Column;
  columns: Column[];
  noRenderFixedColumn: boolean;
};
export const getStylesTableHeaderByColumn = ({
  column,
  columns,
  noRenderFixedColumn,
}: StyleProps) => {
  let style: React.CSSProperties = { zIndex: 10 };
  if (column.fixedColumn && !noRenderFixedColumn) {
    style = {
      zIndex: 12,
      left: calculateColumn(columns, column.apiField),
      width: Number(column.width) as any,
    };
  }
  if (column.colorBorder) {
    style.borderBottom = `2px solid ${column.colorBorder}`;
  }
  if (column.collapsed) {
    style.maxWidth = '40px';
  }
  return style;
};

export const getDataColumnsDefault = (columns: Column[]) => {
  return {
    version: 0,
    tableName: '',
    columns,
    sortColumns: [],
    originalColumns: [],
  };
};

function isStringANumber(str: string) {
  const num = Number(str);
  return typeof num === 'number' && Number.isFinite(num);
}

const converWidth = (width: string | number | null | undefined) => {
  if (width === null) return null;
  if (typeof width === 'string' && String(width) === '') return width;
  if (typeof width === 'string' && isStringANumber(`${width}`)) {
    return Number(width);
  }
  return width;
};

export const convertColumns = (columns: Column[] = []): Column[] => {
  return columns.map((column) => ({
    ...column,
    prefix: column.prefix || undefined,
    suffix: column.suffix || undefined,
    align: column.align || undefined,
    colorBorder: column.colorBorder || undefined,
    tooltip: column.tooltip || undefined,
    direction: column.direction || undefined,
    flexBasis: column.flexBasis || undefined,
    width: converWidth(column.width),
  }));
};

const copyTableToClipboard = async (table: HTMLTableElement) => {
  const html = table.outerHTML;
  const text = table.innerText;

  await navigator.clipboard.write([
    new ClipboardItem({
      'text/html': new Blob([html], {
        type: 'text/html',
      }),
      'text/plain': new Blob([text], {
        type: 'text/plain',
      }),
    }),
  ]);
};

export const generateTableToCopy = async (
  tableRef: React.RefObject<HTMLTableElement>,
) => {
  if (!tableRef.current) {
    return;
  }

  const newTable = document.createElement('table');

  Array.from(tableRef.current.rows).forEach((row) => {
    const clonedRow = row.cloneNode(true) as HTMLTableRowElement;

    Array.from(clonedRow.cells).forEach((cell, index) => {
      const originalCell = row.cells[index];

      const input = originalCell.querySelector('input');
      const textarea = originalCell.querySelector('textarea');
      const select = originalCell.querySelector('select');

      if (input) {
        cell.textContent = input.value;
      } else if (textarea) {
        cell.textContent = textarea.value;
      } else if (select) {
        cell.textContent = select.options[select.selectedIndex]?.text ?? '';
      }

      cell.style.backgroundColor = '#ffffff';
      cell.style.color = '#000000';
    });

    newTable.appendChild(clonedRow);
  });

  document.body.appendChild(newTable);

  try {
    await copyTableToClipboard(newTable);
  } finally {
    document.body.removeChild(newTable);
  }
};
